<template>
  <div class="bill-part-pricing-control">
    <!-- Header InfoBox -->
    <InfoBox
      :label="$t('composites.BillPartPricingControl.title')"
      theme="primary"
      class="q-mb-md">
      <template #body>
        <div class="header-content">
          <p class="text-body2 q-mb-md">{{ $t('composites.BillPartPricingControl.subtitle') }}</p>
          <div class="header-actions">
            <q-btn
              v-if="hasChanges"
              flat
              color="grey-7"
              :label="$t('composites.BillPartPricingControl.actions.reset')"
              :icon="symRoundedRefresh"
              @click="handleReset" />
            <q-btn
              v-if="canProceed"
              unelevated
              color="primary"
              :label="$t('composites.BillPartPricingControl.actions.apply')"
              :icon="symRoundedCheck"
              :loading="state.isProcessing"
              @click="handleApply" />
            <!-- Nothing to control: the parent decides what replaces "apply" (e.g. direct finalize) -->
            <slot
              v-if="isEmpty"
              name="empty-actions" />
            <q-btn
              v-if="!readonly && !isEmpty"
              flat
              color="grey-7"
              :label="$t('composites.BillPartPricingControl.actions.cancel')"
              :icon="symRoundedClose"
              @click="handleCancel" />
          </div>
        </div>
      </template>
    </InfoBox>

    <!-- Loading state InfoBox -->
    <InfoBox
      v-if="state.isLoading"
      :label="$t('composites.BillPartPricingControl.loading')"
      theme="info"
      class="q-mb-md">
      <template #body>
        <div class="loading-state">
          <q-spinner-dots
            size="2em"
            color="primary" />
        </div>
      </template>
    </InfoBox>

    <!-- Error state InfoBox -->
    <InfoBox
      v-else-if="state.error"
      :label="$t('composites.BillPartPricingControl.errors.loadFailed')"
      theme="negative"
      class="q-mb-md">
      <template #body>
        <div class="error-state">
          <div class="row items-center q-gutter-sm">
            <q-icon
              :name="symRoundedError"
              color="negative"
              size="sm" />
            <div class="text-body2 col">{{ state.error }}</div>
            <q-btn
              unelevated
              color="negative"
              :label="$t('common.retry')"
              :icon="symRoundedRefresh"
              @click="handleRetry" />
          </div>
        </div>
      </template>
    </InfoBox>

    <!-- No items state InfoBox -->
    <InfoBox
      v-else-if="isEmpty"
      :label="$t('composites.BillPartPricingControl.noItems')"
      theme="info"
      class="q-mb-md">
      <template #body>
        <div class="no-items-state">
          <q-icon
            :name="symRoundedCheckCircle"
            size="3em"
            color="positive" />
          <p class="text-body2 q-mt-md">{{ $t('composites.BillPartPricingControl.noPricingItems') }}</p>
        </div>
      </template>
    </InfoBox>

    <!-- Main content -->
    <div
      v-else
      class="pricing-control-content">
      <!-- Pricing items list -->
      <div class="pricing-items-container">
        <slot name="pricing-items">
          <div class="pricing-items-list">
            <PartPricingRow
              v-for="item in state.items"
              :key="item.id"
              :item="item"
              :readonly="readonly"
              :highlight-threshold="5"
              :warning-threshold="50"
              @action-change="handleActionChange" />
          </div>
        </slot>
      </div>

      <!-- Validation Display -->
      <div
        v-if="validation.validationSummary.value.errorCount > 0 || validation.validationSummary.value.warningCount > 0"
        class="validation-container">
        <ValidationDisplay
          :errors="validation.validationSummary.value.errors"
          :warnings="validation.validationSummary.value.warnings"
          :infos="validation.validationSummary.value.infos"
          :show-warnings="true"
          :show-infos="false"
          :show-summary="false" />
      </div>

      <!-- Summary InfoBox -->
      <InfoBox
        v-if="showSummary && state.items.length > 0"
        :label="$t('composites.BillPartPricingControl.summary.title')"
        theme="default"
        class="summary-container q-mt-md">
        <template #body>
          <div class="summary-content">
            <div class="row q-col-gutter-md">
              <div class="col-12 col-sm-4">
                <div class="summary-stat">
                  <div class="text-overline text-grey-7">{{ $t('composites.BillPartPricingControl.summary.totalItems') }}</div>
                  <div class="text-h6">{{ state.items.length }}</div>
                </div>
              </div>
              <div class="col-12 col-sm-4">
                <div class="summary-stat">
                  <div class="text-overline text-grey-7">{{ $t('composites.BillPartPricingControl.summary.itemsToUpdate') }}</div>
                  <div class="text-h6 text-positive">{{ itemsToUpdate.length }}</div>
                </div>
              </div>
              <div class="col-12 col-sm-4">
                <div class="summary-stat">
                  <div class="text-overline text-grey-7">{{ $t('composites.BillPartPricingControl.summary.totalImpact') }}</div>
                  <div
                    class="text-h6"
                    :class="totalPriceImpact >= 0 ? 'text-positive' : 'text-negative'">
                    {{ formatCurrency(totalPriceImpact) }}
                  </div>
                </div>
              </div>
            </div>
            <slot name="summary"></slot>
          </div>
        </template>
      </InfoBox>
    </div>

    <!-- Processing overlay -->
    <q-inner-loading
      :showing="state.isProcessing"
      color="primary">
      <q-spinner-dots size="50px" />
      <div class="text-center q-mt-md">
        {{ $t('composites.BillPartPricingControl.processing') }}
      </div>
    </q-inner-loading>
  </div>
</template>

<script setup lang="ts">
import { symRoundedCheck, symRoundedCheckCircle, symRoundedClose, symRoundedError, symRoundedRefresh } from '@quasar/extras/material-symbols-rounded'
import { useQuasar } from 'quasar'
import InfoBox from 'src/CrudManager/Core/composites/Ui/InfoBox/InfoBox.vue'
import { showErrorNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { computed, onMounted, onUnmounted, provide, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PartPricingRow from './components/PartPricingRow.vue'
import ValidationDisplay from './components/ValidationDisplay.vue'
import { useBillPartPricingApi } from './composables/useBillPartPricingApi'
import { useBillPartPricingLogic } from './composables/useBillPartPricingLogic'
import { useBillPartPricingState } from './composables/useBillPartPricingState'
import { useBillPartPricingValidation } from './composables/useBillPartPricingValidation'
import type { PricingAction } from './constants'
import type { PricingControlEmits, PricingControlProps } from './types'

// Props and emits
const props = withDefaults(defineProps<PricingControlProps>(), {
  readonly: false,
  showSummary: true,
})

const emit = defineEmits<PricingControlEmits>()

// Composables
const $q = useQuasar()
const { t } = useI18n()
const stateComposable = useBillPartPricingState()
const { state } = stateComposable
const validation = useBillPartPricingValidation(state)
const logic = useBillPartPricingLogic(state, emit, props.transactionId)
const api = useBillPartPricingApi(state)

// Computed properties
const canProceed = computed(() => stateComposable.canProceedWithValidation(validation, props.readonly))
const isEmpty = computed(() => !state.value.isLoading && !state.value.error && state.value.items.length === 0)
const hasChanges = computed(() => stateComposable.hasChanges.value)
const itemsToUpdate = computed(() => stateComposable.itemsToUpdate.value)
const totalPriceImpact = computed(() => stateComposable.totalPriceImpact.value)

// Helper methods
const formatCurrency = (amount: number, currency = 'CAD'): string => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

// Provide context for child components
const context = {
  state,
  validation,
  validationErrors: validation.validationErrors,
  validationWarnings: validation.validationWarnings,
  hasItemErrors: validation.hasItemErrors,
  ...stateComposable,
  ...logic,
  ...api,
}

provide('composites.BillPartPricingControl', context)

// Event handlers
const handleApply = async () => {
  if (props.readonly) return

  try {
    // Validate before applying
    const validationErrors = logic.validatePricingDecisions()
    if (validationErrors.length > 0) {
      showErrorNotification(validationErrors[0])
      return
    }

    // Confirm with user if there are significant changes
    const significantChanges = stateComposable.itemsWithSignificantChanges.value
    if (significantChanges.length > 0) {
      const confirmed = await new Promise<boolean>((resolve) => {
        $q.dialog({
          title: t('common.confirm'),
          message: t('composites.BillPartPricingControl.warnings.significantIncrease'),
          ok: {
            label: t('common.continue'),
            color: 'primary',
          },
          cancel: {
            label: t('common.cancel'),
            color: 'grey',
            flat: true,
          },
        })
          .onOk(() => resolve(true))
          .onCancel(() => resolve(false))
      })

      if (!confirmed) return
    }

    await logic.applyPricingDecisions()

    // The logic emits complete only after the API confirms success.
  } catch (error) {
    console.error('Failed to apply pricing decisions:', error)
  }
}

const handleReset = async () => {
  if (props.readonly) return

  const confirmed = await new Promise<boolean>((resolve) => {
    $q.dialog({
      title: t('common.confirm'),
      message: t('composites.BillPartPricingControl.warnings.unsavedChanges'),
      ok: {
        label: t('composites.BillPartPricingControl.actions.reset'),
        color: 'negative',
      },
      cancel: {
        label: t('common.cancel'),
        color: 'grey',
        flat: true,
      },
    })
      .onOk(() => resolve(true))
      .onCancel(() => resolve(false))
  })

  if (confirmed) {
    logic.resetChanges()
  }
}

const handleRetry = () => {
  if (props.transactionId) {
    logic.loadPricingAnalysis(props.transactionId)
  }
}

const handleActionChange = (itemId: string, action: PricingAction) => {
  logic.setItemAction(itemId, action)
}

const handleCancel = () => {
  if (hasChanges.value) {
    $q.dialog({
      title: t('common.confirm'),
      message: t('composites.BillPartPricingControl.warnings.unsavedChanges'),
      ok: {
        label: t('composites.BillPartPricingControl.actions.cancel'),
        color: 'negative',
      },
      cancel: {
        label: t('common.stay'),
        color: 'grey',
        flat: true,
      },
    }).onOk(() => {
      emit('cancel')
    })
  } else {
    emit('cancel')
  }
}

// Lifecycle
onMounted(() => {
  // Auto-load pricing analysis
  if (props.transactionId) {
    logic.loadPricingAnalysis(props.transactionId)
  }
})

// Watch for transaction ID changes
watch(
  () => props.transactionId,
  (newTransactionId) => {
    if (newTransactionId) {
      logic.loadPricingAnalysis(newTransactionId)
    }
  },
)

// Watch for changes and emit change event
watch(
  () => hasChanges.value,
  (newHasChanges) => {
    emit('change', newHasChanges)
  },
)

// Warn user about unsaved changes when leaving
const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
  if (hasChanges.value) {
    event.preventDefault()
    event.returnValue = t('composites.BillPartPricingControl.warnings.unsavedChanges')
    return event.returnValue
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', beforeUnloadHandler)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', beforeUnloadHandler)
})
</script>

<style scoped>
.bill-part-pricing-control {
  width: 100%;
  position: relative;
}

.loading-state,
.error-state,
.no-items-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
}

.no-items-state {
  color: var(--q-grey-7);
}

.header-content {
  width: 100%;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.pricing-control-content {
  width: 100%;
}

.pricing-items-container {
  margin-bottom: 1.5rem;
}

.pricing-items-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.validation-container {
  margin-bottom: 1.5rem;
}

.summary-container {
  margin-top: 1.5rem;
}

.summary-content {
  width: 100%;
}

.summary-stat {
  padding: 1rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
}

/* Responsive design */
@media (max-width: 768px) {
  .header-actions {
    justify-content: flex-start;
    width: 100%;
    gap: 0.5rem;
  }

  .header-actions .q-btn {
    flex: 1;
    min-width: 0;
  }

  .loading-state,
  .error-state,
  .no-items-state {
    padding: 1.5rem 1rem;
  }

  .summary-stat {
    padding: 0.75rem;
  }
}
</style>
